import { Fragment, useEffect, useRef, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import DownloadSelect from '../../../components/HoverableCard/DownloadSelect';
import PaginationStyled from '../../../components/Pagination';
import {
  Button,
  IconArrowRight as ArrowIcon,
  IconCheckmark1 as CheckIcon,
  IconInfoCircle as InfoCircle,
  IconReset as ResetIcon,
} from '../../../core';
import { ReactComponent as ComparisonIcon } from '../../../images/icons/charts-comparison.svg';
import { ReactComponent as CorrelationIcon } from '../../../images/icons/charts-correlation.svg';
import { ReactComponent as DistributionIcon } from '../../../images/icons/charts-distribution.svg';
import { ReactComponent as PorportionIcon } from '../../../images/icons/charts-proportion.svg';
import { ReactComponent as TrendIcon } from '../../../images/icons/charts-trend.svg';
import { ReactComponent as CheckboxCheckedIcon } from '../../../images/icons/checkbox-checked.svg';
import { ReactComponent as CheckboxIcon } from '../../../images/icons/checkbox.svg';
import { ReactComponent as RadioCheckedIcon } from '../../../images/icons/radio-checked.svg';
import { ReactComponent as RadioIcon } from '../../../images/icons/radio.svg';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import theme from '../../../theme';
import { CHART_DESCRIPTIONS, ChartRoute } from '../../DataVizualization/chartDescriptions';

import { ChartSectionContainer, ChartSectionDivider, DataVizContainer } from './ChartMatch.styled';
import { chartsMocks } from './mockData/chartsMocks';
import { filtertMocks } from './mockData/filtertMocks';
import { stepsMocks } from './mockData/stepsMocks';

type ChartMatchStructureProps = {
  className?: string;
};

type Chart = {
  id: number;
  type: string;
  title: string;
  description: string;
  url: string;
  image: string;
  icon?: JSX.Element;
  types: string[];
};

const tooltipBorder = `1px solid ${theme.neutralBorder}`;

const groupedByType = chartsMocks.reduce(
  (acc, chart) => {
    if (!acc[chart.type]) {
      acc[chart.type] = [];
    }
    acc[chart.type].push(chart);
    return acc;
  },
  {} as Record<string, Chart[]>,
);

const ChartMatchStructure = ({ className = '' }: ChartMatchStructureProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [filteredCharts, setFilteredCharts] = useState(chartsMocks); // Initialize with all charts
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [selectOpenChartId, setSelectOpenChartId] = useState<number | null>(null);
  const [hoveredChartId, setHoveredChartId] = useState<number | null>(null);

  useEffect(() => {
    const selectedKeys = Object.values(filters);
    if (selectedKeys.length === 0) {
      setFilteredCharts(chartsMocks);
    }

    /* let purposeFilteredCharts;
    if (filters.purpose) {
      const selectedKeys = Array.isArray(filters.purpose) ? filters.purpose : [filters.purpose];
      purposeFilteredCharts = charts.filter((chart) =>
        selectedKeys.some((key) => chart.types.includes(key)),
      );
    } else {
      purposeFilteredCharts = charts;
    } */

    if (selectedKeys.length > 0) {
      const flatKeys = selectedKeys.flat();
      const newFilteredCharts = chartsMocks.filter((chart) =>
        flatKeys.every((key) => chart.types.includes(key)),
      );
      setFilteredCharts(newFilteredCharts);
    }
  }, [filters]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollableContainerRef.current) {
        setIsScrolled(scrollableContainerRef.current.scrollTop > 0);
      }
    };

    const container = scrollableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleStepChange = (nextStep: number, key: string, value: string) => {
    setFilters((prevFilters) => {
      if (key === 'purpose') {
        const currentValues: string[] = (prevFilters[key] as string[]) || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v: string) => v !== value) // Remove value if already selected
          : [...currentValues, value]; // Add value if not selected

        return {
          ...prevFilters,
          [key]: newValues,
        };
      }
      return {
        ...prevFilters,
        [key]: value,
      };
    });

    if (nextStep > currentStep) setCurrentStep(nextStep);
  };

  useEffect(() => {
    if (stepRefs.current[currentStep] && scrollableContainerRef.current) {
      const container = scrollableContainerRef.current;
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = stepRefs.current[currentStep]?.getBoundingClientRect().top || 0;
      const scrollPosition = elementTop - containerTop + container.scrollTop;
      container.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }, [currentStep]);

  const renderCheckbox = (stepKey: string, optionKey: string, index: number) => {
    const CheckedIcon = index === 0 ? CheckboxCheckedIcon : RadioCheckedIcon;
    const UncheckedIcon = index === 0 ? CheckboxIcon : RadioIcon;

    if (Array.isArray(filters[stepKey])) {
      return filters[stepKey].includes(optionKey) ? <CheckedIcon /> : <UncheckedIcon />;
    }
    return filters[stepKey] === optionKey ? <CheckedIcon /> : <UncheckedIcon />;
  };

  const renderChartPreview = (chart: Chart) => {
    const shouldShowDetails = hoveredChartId === chart.id || selectOpenChartId === chart.id;

    return (
      <div
        key={chart.id}
        className={`chart-block ${shouldShowDetails ? 'is-hovered' : ''}`}
        onMouseEnter={() => setHoveredChartId(chart.id)}
        onMouseLeave={() => setHoveredChartId(null)}
      >
        <div className="chart-preview">
          <img className="chart-img img-responsive" src={chart.image} alt="" />
          <div className="chart-title">
            <div className="icon-container">{chart.icon}</div>
            {chart.title}
          </div>
        </div>
        <div className="chart-details">
          <div className="chart-title">
            <div className="icon-container">{chart.icon}</div>
            {chart.title}
          </div>
          <div className="chart-description">
            <div className="top-buttons">
              {chart.types.includes('purpose_comp') && (
                <div className="type-badge">
                  <ComparisonIcon />
                  Comparison
                </div>
              )}
              {chart.types.includes('purpose_corr') && (
                <div className="type-badge">
                  <CorrelationIcon />
                  Correlation
                </div>
              )}
              {chart.types.includes('purpose_dist') && (
                <div className="type-badge">
                  <DistributionIcon />
                  Distribution
                </div>
              )}
              {chart.types.includes('purpose_prop') && (
                <div className="type-badge">
                  <PorportionIcon />
                  Proportion
                </div>
              )}
              {chart.types.includes('purpose_trend') && (
                <div className="type-badge">
                  <TrendIcon />
                  Trend
                </div>
              )}
            </div>
            {CHART_DESCRIPTIONS[chart.url as ChartRoute]}
          </div>
          <div className="chart-buttons">
            <a href={chart.url} title="">
              Details
            </a>
            <div className="right">
              <DownloadSelect
                onOpenChange={(isOpen) => setSelectOpenChartId(isOpen ? chart.id : null)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          title="ChartMatch"
          breadcrumbs={[
            { label: 'Design System', url: '/data-visualization' },
            { label: 'ChartMatch', url: '#' },
          ]}
        />
        <div className="page-container">
          <div className="page-content">
            <div className="page-content-with-quiz-wrapper">
              <div className="sticky">
                {Object.keys(filters).length === 0 && (
                  <div className="quiz-tip">
                    Leverage the Data Viz Filter feature for tailored visualization recommendations
                    based on your specific needs.
                  </div>
                )}

                <div className="quiz">
                  <h2>Data Viz Filtering</h2>
                  <div className="quiz-text">
                    Answer questions to discover the visualization that best suits your needs.
                    <div className="transparent-element" style={{ opacity: isScrolled ? 1 : 0 }} />
                  </div>

                  <DataVizContainer>
                    {filtertMocks.map((filter) => (
                      <ReactTooltip
                        key={filter.id}
                        noArrow
                        className="tooltip-w-shadow"
                        id={`tooltip-${filter.id}`}
                        variant="light"
                        opacity={1}
                        border={tooltipBorder}
                        place="bottom"
                      >
                        <div className="tooltip-content">
                          <img src={filter.image} alt="" />
                          <p>{filter.description}</p>
                        </div>
                      </ReactTooltip>
                    ))}
                    <ReactTooltip
                      className="tooltip-w-shadow"
                      id="tooltip-dimensions"
                      variant="light"
                      opacity={1}
                      border={tooltipBorder}
                      place="right"
                    >
                      <div className="tooltip-content badges">
                        <div className="badge-row">
                          <BadgeStyled className="info">Race/Ehtnicity</BadgeStyled>
                          <BadgeStyled className="info">Age Group</BadgeStyled>
                        </div>
                        <div className="badge-row">
                          <BadgeStyled className="info">County</BadgeStyled>
                          <BadgeStyled className="info">Health Plan</BadgeStyled>
                          <BadgeStyled className="info">Sex</BadgeStyled>
                          <BadgeStyled className="info">...</BadgeStyled>
                        </div>
                      </div>
                    </ReactTooltip>
                    <ReactTooltip
                      className="tooltip-w-shadow"
                      id="tooltip-numeric"
                      variant="light"
                      opacity={1}
                      border={tooltipBorder}
                      place="right"
                    >
                      <div className="tooltip-content badges">
                        <div className="badge-row">
                          <BadgeStyled className="info">Members</BadgeStyled>
                          <BadgeStyled className="info">Population</BadgeStyled>
                        </div>
                        <div className="badge-row">
                          <BadgeStyled className="info">Payment Allocation</BadgeStyled>
                          <BadgeStyled className="info">...</BadgeStyled>
                        </div>
                      </div>
                    </ReactTooltip>
                    <ReactTooltip
                      className="tooltip-w-shadow"
                      id="tooltip-location"
                      variant="light"
                      opacity={1}
                      border={tooltipBorder}
                      place="right"
                    >
                      <div className="tooltip-content badges">
                        <div className="badge-row">
                          <BadgeStyled className="info">County</BadgeStyled>
                          <BadgeStyled className="info">Zip Code</BadgeStyled>
                        </div>
                        <div className="badge-row">
                          <BadgeStyled className="info">Region</BadgeStyled>
                          <BadgeStyled className="info">...</BadgeStyled>
                        </div>
                      </div>
                    </ReactTooltip>
                    <ReactTooltip
                      className="tooltip-w-shadow"
                      id="tooltip-subdimensions"
                      variant="light"
                      opacity={1}
                      border={tooltipBorder}
                      place="right"
                    >
                      <div className="tooltip-content badges">
                        <div className="badge-row">
                          <BadgeStyled className="info">Hispanic</BadgeStyled>
                          <BadgeStyled className="info">80-84</BadgeStyled>
                          <BadgeStyled className="info">Alameda</BadgeStyled>
                        </div>
                        <div className="badge-row">
                          <BadgeStyled className="info">CalOptima</BadgeStyled>
                          <BadgeStyled className="info">Male</BadgeStyled>
                          <BadgeStyled className="info">...</BadgeStyled>
                        </div>
                      </div>
                    </ReactTooltip>
                    <div className="quiz-steps" ref={scrollableContainerRef}>
                      {stepsMocks.map((step, index) => (
                        <div
                          key={step.id}
                          ref={(el) => {
                            stepRefs.current[index] = el;
                          }}
                          className={`quiz-step ${currentStep >= index ? 'active' : ''}`}
                        >
                          <div className="quiz-step-title">
                            <span>{step.id}</span>
                            {step.title}
                          </div>
                          {step.options.map((option) => (
                            <button
                              key={`${step.key}-${option.key}`}
                              onClick={() =>
                                handleStepChange(
                                  index === 0 ? index : index + 1,
                                  step.key,
                                  option.key,
                                )
                              }
                              type="button"
                              className="quiz-option"
                            >
                              <div className="quiz-option-checkbox">
                                {renderCheckbox(step.key, option.key, index)}
                              </div>
                              <div className="quiz-option-title">
                                {option.text}
                                <InfoCircle
                                  data-tooltip-id={`tooltip-${option.tooltipContent.id}`}
                                />
                              </div>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                    {Object.keys(filters).length === 4 && (
                      <div className="filter-complete">
                        <CheckIcon />
                        <div className="filter-complete-tip">
                          <div className="filter-complete-title">Charts has been filtered</div>
                          <div className="filter-complete-text">
                            Please have an option from the list to view additional details.
                          </div>
                        </div>
                      </div>
                    )}
                    {currentStep > 0 && (
                      <Button
                        iconAfter={<ResetIcon />}
                        className="reset-filters"
                        size="M"
                        onClick={() => {
                          setFilters({});
                          setCurrentStep(0);
                        }}
                      >
                        Reset selections
                      </Button>
                    )}
                    {Object.keys(filters).length === 1 && currentStep === 0 && (
                      <Button
                        className="reset-filters"
                        size="M"
                        iconAfter={<ArrowIcon />}
                        onClick={() => {
                          setCurrentStep(1);
                        }}
                      >
                        Confirm
                      </Button>
                    )}
                  </DataVizContainer>
                </div>
              </div>
              <div className="page-content-with-quiz">
                {Object.keys(filters).length === 0 ? (
                  <>
                    {Object.entries(groupedByType).map(([type, charts]) => (
                      <Fragment key={type}>
                        <ChartSectionContainer>
                          <h2>{type}</h2>
                          <div className="charts-container">
                            {charts.map((chart) => renderChartPreview(chart))}
                          </div>
                        </ChartSectionContainer>
                        <ChartSectionDivider />
                      </Fragment>
                    ))}
                  </>
                ) : (
                  <div className="charts-container">
                    {filteredCharts.map((chart) => renderChartPreview(chart))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <PaginationStyled previous="Get Started" previousLink="/" />
      </div>
    </div>
  );
};

const ChartMatch = styled(ChartMatchStructure)`
  ${commonPageContentStyles};

  display: block;
  height: 100%;
  overflow: unset;
  width: 100%;

  .content {
    overflow-x: hidden;
    margin: 0 auto;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .sticky {
    position: sticky;
    top: 1rem;
  }

  .quiz {
    width: 21.25rem;
    border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
    border-radius: 0.5rem;
    padding: 24px 4px 24px 24px;
    position: relative;

    > h2 {
      ${textStyle('h4Sb')}
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
      margin-bottom: 0.45rem;
    }

    &-tip {
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.25rem;
      margin-bottom: 2rem;
      margin-top: 1.25rem;
      width: 21.25rem;
      letter-spacing: -0.01rem;
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    .tooltip-w-shadow {
      box-shadow:
        /* tokens-allow: pending design decision */
        0rem 0.25rem 0.5rem 0.125rem rgba(20, 28, 44, 0.06),
        /* tokens-allow: pending design decision */ 0rem 0.125rem 0.25rem 0rem
          rgba(20, 28, 44, 0.06);
      padding: 1rem;
    }

    .filter-complete {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem;
      background: ${({ theme }) => theme.primaryBackground};
      margin-top: 2rem;
      border-radius: 0.25rem;

      > svg {
        color: ${({ theme }) => theme.primaryText};
        min-width: 1.25rem;
      }

      &-title {
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 600;
        line-height: 1.25rem;
        color: ${({ theme }) => theme.primaryText};
      }

      &-text {
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.25rem;
      }
    }

    .tooltip-content {
      display: flex;
      max-width: 23rem;
      align-items: flex-start;
      font-size: 0.75rem;
      line-height: 0.875rem;
      font-weight: 400;
      gap: 0.75rem;

      img {
        min-width: 50%;
        width: 50%;
      }

      &.badges {
        gap: 0.375rem;
        flex-wrap: wrap;
        width: auto;
        flex-direction: column;

        .info {
          white-space: nowrap;
          font-weight: 400;
        }

        .badge-row {
          display: flex;
          gap: 0.375rem;
        }
      }
    }

    .reset-filters {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 1.5rem;

      .button-name,
      .button-icon {
        color: ${({ theme }) => theme.neutralTextWeak};
      }
    }

    &-text {
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.25rem;
      position: relative;
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    .transparent-element {
      position: absolute;
      top: 100%;
      width: 100%;
      height: 5rem;
      transition: opacity 0.3s;
      background: linear-gradient(
        180deg,
        ${({ theme }) => theme.neutralBackgroundBase} 0%,
        ${({ theme }) => theme.neutralBackgroundTransparent} 100%
      );
      pointer-events: none;
    }

    &-steps {
      overflow-y: auto;
    }

    &-step {
      max-height: 0;
      display: none;

      &-title {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        flex-shrink: 0;
        margin-top: 1.5rem;
        margin-bottom: 1.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.25rem;

        > div {
          > svg {
            margin-left: 0.25rem;
            margin-right: 0.25rem;
            vertical-align: middle;
          }
        }

        > span {
          padding: 0.25rem;
          background-color: ${({ theme }) => theme.primaryAction};
          border-radius: 0.25rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${({ theme }) => theme.primaryTextStrong};
          min-width: 1.375rem;
          min-height: 1.375rem;
          font-size: 0.75rem;
          font-weight: 500;
          line-height: 0.875rem;
          flex-shrink: 0;
          margin-right: 0.5rem;
        }
      }

      &.active {
        max-height: 40rem;
        display: block;
      }
    }

    &-option {
      display: flex;
      cursor: pointer;
      appearance: none;
      background: transparent;
      border: none;
      width: 100%;
      padding-left: 0;
      padding-right: 0;

      &:not(:first-of-type) {
        padding-top: 0.5rem;
      }

      &-title {
        border-bottom: 1px dashed ${({ theme }) => theme.neutralBorder};
        display: flex;
        justify-content: space-between;
        line-height: 1.25rem;
        font-size: 0.875rem;
        font-weight: 400;
        padding-bottom: 0.84rem;
        width: 100%;
        text-align: left;

        svg {
          margin-left: 0.25rem;
          color: ${({ theme }) => theme.primaryText};
        }
      }
      &-checkbox {
        margin-right: 0.5rem;
      }
    }
  }

  .charts-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-gap: 1rem;
    padding-bottom: 1.4rem;

    @media (width <= 1200px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    .chart-preview {
      position: relative;
      transform: translateY(0);
      transition: transform 0.2s ease-in-out;
      padding-top: 0.5rem;
    }

    .chart-block {
      border-radius: 0.5rem;
      border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      overflow: hidden;
      background: transparent;
      position: relative;
      padding: 0;

      &:hover,
      &.is-hovered {
        .chart-preview {
          transform: translateY(-100%);
        }

        .chart-details {
          top: 0;
        }
      }

      .type-badge {
        padding: 0.375rem;
        font-size: 0.625rem;
        line-height: 0.875rem;
        display: flex;
        align-items: center;
        color: ${({ theme }) => theme.neutralText};
        border-radius: 4px;
        background: ${({ theme }) => theme.neutralBackground};

        svg {
          padding-right: 0.375rem;
          margin-right: 0.375rem;
          border-right: 1px solid ${({ theme }) => theme.neutralBorder};
          color: ${({ theme }) => theme.neutralTextWeak};
          width: 1.25rem;
        }
      }

      .chart-details {
        position: absolute;
        display: flex;
        flex-direction: column;
        top: 100%;
        //top: 0;
        transition: top 0.2s ease-in-out;
        height: 100%;
        width: 100%;
        z-index: 10;
        background:
          linear-gradient(
            90deg,
            ${({ theme }) => theme.neutralBackgroundTransparent} 32.12%,
            /* tokens-allow: pending design decision */ rgba(255, 255, 255, 0.5) 93.2%
          ),
          /* tokens-allow: pending design decision */ rgba(116, 124, 137, 0.08);

        .chart-title {
          margin-top: 0;
          padding-top: 1rem;
          background: transparent;

          .icon-container {
            color: ${({ theme }) => theme.primaryTextStrong};
            background-color: ${({ theme }) => theme.primaryAction};
          }
        }

        .chart-description {
          margin-top: 0;
          flex: 1;
          color: ${({ theme }) => theme.neutralTextWeak};
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 0.75rem;
          line-height: 0.875rem;
          padding-left: 0.75rem;
          padding-right: 0.75rem;

          .top-buttons {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }
        }

        .chart-buttons {
          margin-top: 0;
          padding: 1rem 0.75rem;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .right {
            display: flex;
            gap: 0.5rem;
          }

          a {
            font-weight: 500;
            font-size: 0.875rem;
          }
        }
      }

      .chart-img {
        width: 83%;
        height: auto;
        display: block;
        margin: 0 auto 0.5rem;
      }

      .chart-title {
        margin-top: 0.5rem;
        background-color: ${({ theme }) => theme.neutralBackground};
        padding: 0.5rem 0.75rem;
        display: flex;
        align-items: center;

        .icon-container {
          margin-right: 0.5rem;
          color: ${({ theme }) => theme.primaryText};
          padding: 0.125rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0rem 0.125rem 0.25rem 0.0625rem #141c2c0a; /* tokens-allow: pending design decision */
          background: ${({ theme }) => theme.neutralBackgroundBase};
          border-radius: 0.25rem;
        }
      }
    }
  }
`;

export default ChartMatch;
